import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Pause, Play, X, Captions, Keyboard, Loader2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SageParticleOrb from '../components/SageParticleOrb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AutoCoaching — hands-free voice-to-voice coaching via ElevenLabs Agents.
 *
 * Flow:
 *   1. POST /api/coaching/agent/start → { session_id, signed_url, dynamic_variables }
 *   2. conversation.startSession({ signedUrl, dynamicVariables }) via @elevenlabs/react
 *   3. SDK handles mic + audio playback + WS. We handle the UI state.
 *   4. User taps End Session → conversation.endSession() → POST /finalize to
 *      pull transcript + merge into coaching_sessions.interactions.
 *
 * UI controls (phase 1):
 *   - "Hold on, I'm thinking" button — pauses by muting the mic locally.
 *     SDK's `micMuted` state silences the user side; the agent is also told
 *     via a system message so it stops talking. Tapping again re-enables.
 *   - Captions toggle — defaults off. When on, shows live user transcript
 *     + agent response under the orb.
 *   - "Or type your question" — sends a text message as the user's turn via
 *     conversation.sendUserMessage(text).
 *   - End Session — stops the agent + finalizes transcript.
 */

const AutoCoachingInner = ({ onExit }) => {
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('Preparing…');
  const [isPaused, setIsPaused] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const [liveUserTranscript, setLiveUserTranscript] = useState('');
  const [lastAgentMessage, setLastAgentMessage] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [isEnding, setIsEnding] = useState(false);
  const [startError, setStartError] = useState(false);
  const sessionStartedRef = useRef(false);
  // Did the WS actually reach a "connected" state? Used to tell the
  // difference between a user-ended session and a handshake failure
  // (e.g. ElevenLabs rejecting our voice override). On a handshake
  // failure we auto-retry without the override — see startSession.
  const didConnectRef = useRef(false);
  // Pending start payload for the fallback retry. Holds the signed_url
  // + dynamicVariables from the backend so the retry doesn't need to
  // hit /start again (that would burn another signed URL).
  const pendingStartRef = useRef(null);
  // Have we already retried without overrides? Prevents infinite loops.
  const overrideRetryRef = useRef(false);

  // Retry the SDK session WITHOUT the voice override. ElevenLabs rejects
  // overrides that aren't whitelisted on the agent's platform settings,
  // which closes the WS during handshake. We fetch a fresh signed URL
  // (the original may have been consumed by the failed handshake) and
  // fall back to the agent's default voice so the session still works.
  const retryWithoutOverride = useCallback(async () => {
    if (overrideRetryRef.current) return false;
    overrideRetryRef.current = true;
    didConnectRef.current = false;
    setStatus('Connecting to SAGE…');
    // Let the SDK fully tear down the failed WS before we start a
    // fresh session — otherwise startSession can inherit a half-closed
    // socket state and reject the second connection too.
    try { await conversation.endSession?.(); } catch (err) {
      // SDK may already be disconnecting; expected when we retry.
      if (process.env.NODE_ENV !== 'production') console.debug('retry endSession:', err);
    }
    await new Promise((r) => setTimeout(r, 400));
    const token = localStorage.getItem('sage_token');
    try {
      const { data } = await axios.post(
        `${API}/coaching/agent/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSessionId(data.session_id);
      pendingStartRef.current = {
        signed_url: data.signed_url,
        dynamic_variables: data.dynamic_variables || {},
        had_override: false,
      };
      // eslint-disable-next-line no-use-before-define
      await conversation.startSession({
        signedUrl: data.signed_url,
        connectionType: 'websocket',
        dynamicVariables: data.dynamic_variables || {},
        // Intentionally no overrides this time.
      });
      return true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.debug('Retry without override failed:', err);
      setStatus('Could not start');
      setStartError(true);
      return false;
    }
    // conversation is pulled from closure; added below via linter disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ElevenLabs SDK hook — manages the WebSocket, mic, and audio playback.
  // We register listeners for the client events we care about.
  const conversation = useConversation({
    onConnect: () => {
      didConnectRef.current = true;
      setStatus('SAGE is listening');
    },
    onDisconnect: () => {
      // Handshake failure (never reached connected) with overrides sent?
      // Most commonly ElevenLabs rejecting an un-whitelisted voice
      // override. Silently retry once without overrides.
      if (!didConnectRef.current && !overrideRetryRef.current && pendingStartRef.current?.had_override) {
        retryWithoutOverride();
        return;
      }
      setStatus('Session ended');
    },
    onError: (err) => {
      // SDK forwards agent + WS errors here.
      if (process.env.NODE_ENV !== 'production') console.debug('ElevenLabs error:', err);
      // Same handshake-failure path as onDisconnect — some SDK builds
      // fire onError first, others onDisconnect first.
      if (!didConnectRef.current && !overrideRetryRef.current && pendingStartRef.current?.had_override) {
        retryWithoutOverride();
        return;
      }
      toast.error('Something went off-track — try starting a new session.');
    },
    onMessage: ({ message, source }) => {
      // `source` is 'user' or 'ai' depending on who sent the message.
      // Captures finalized transcript turns as they come in.
      if (source === 'user') {
        setLiveUserTranscript(message || '');
      } else if (source === 'ai') {
        setLastAgentMessage(message || '');
      }
    },
    onStatusChange: ({ status: s }) => {
      // s is e.g. 'connected', 'disconnected', 'connecting'
      if (s === 'connecting') setStatus('Connecting to SAGE…');
    },
    onModeChange: ({ mode }) => {
      // mode: 'speaking' | 'listening' — drives the orb state.
      if (mode === 'speaking') setStatus('SAGE is speaking');
      else if (mode === 'listening' && !isPaused) setStatus('SAGE is listening');
    },
  });

  // Kick off the session on first mount.
  const startSession = useCallback(async () => {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    const token = localStorage.getItem('sage_token');
    try {
      // Ensure mic permission UP FRONT — the SDK will request it too but
      // asking first makes the "mic prompt → error" sequence cleaner on
      // mobile Safari where the permission dialog can race the WS open.
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      toast.error('SAGE needs microphone access to talk with you.');
      setStatus('Mic permission needed');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/coaching/agent/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSessionId(data.session_id);
      // Voice override is sent per-session so Auto mode respects the
      // user's Voice Selector. ElevenLabs rejects any override that
      // isn't whitelisted on the agent — if the handshake closes
      // immediately, make sure `tts.voice_id` is toggled ON in the
      // agent's Security → Overrides panel in the ElevenLabs dashboard.
      // The retry mechanism below (onDisconnect / onError) catches the
      // rejection and falls back to the agent's default voice so the
      // session still works even if the toggle is off.
      const hasVoiceOverride = Boolean(data.voice_id);
      // NOTE: The personalized opening greeting (`data.opening_greeting`)
      // is NOT sent as an `agent.firstMessage` override — that would
      // require the "First message" override toggle to be whitelisted
      // in the ElevenLabs agent dashboard, and shipping it blind caused
      // the handshake to be rejected (session ended instantly). Instead
      // it rides along as a `{{opening_greeting}}` dynamic variable in
      // `data.dynamic_variables` — set the agent's First Message field
      // in the ElevenLabs dashboard to `{{opening_greeting}}` to pick
      // it up without flipping any override whitelist.
      // Stash so onDisconnect/onError can retry without overrides if
      // the ElevenLabs agent rejects our voice override (not whitelisted
      // in the agent's platform settings).
      pendingStartRef.current = {
        signed_url: data.signed_url,
        dynamic_variables: data.dynamic_variables || {},
        had_override: hasVoiceOverride,
      };
      didConnectRef.current = false;
      overrideRetryRef.current = false;
      await conversation.startSession({
        signedUrl: data.signed_url,
        connectionType: 'websocket',
        dynamicVariables: data.dynamic_variables || {},
        ...(hasVoiceOverride
          ? { overrides: { tts: { voiceId: data.voice_id } } }
          : {}),
      });
    } catch (err) {
      // Beta monthly cap exhausted — backend returns 429 with a
      // structured detail object: { code, message, snapshot }. We
      // surface the human message and bounce back to the dashboard
      // where the usage meter + "Request more time" CTA live.
      const detailRaw = err?.response?.data?.detail;
      const isCapHit = err?.response?.status === 429
        && typeof detailRaw === 'object'
        && detailRaw?.code === 'monthly_limit_reached';
      if (isCapHit) {
        toast.error(detailRaw.message, { duration: 6000 });
        setStatus('Monthly limit reached');
        setStartError(true);
        sessionStartedRef.current = false;
        // Give the toast a beat to land, then take the user back to
        // their dashboard so they can request more time.
        setTimeout(() => onExit?.(), 1200);
        return;
      }
      const detail = (typeof detailRaw === 'string' ? detailRaw : detailRaw?.message)
        || 'Could not start Auto Conversation.';
      toast.error(detail);
      setStatus('Could not start');
      setStartError(true);
      sessionStartedRef.current = false;
    }
  }, [conversation, onExit]);

  useEffect(() => {
    startSession();
    // Cleanup: close the session if the user navigates away without
    // tapping End Session (tab close, back button, hot reload).
    return () => {
      try {
        if (conversation?.status === 'connected') {
          conversation.endSession?.();
        }
      } catch (err) {
        // Non-fatal — the SDK may be mid-teardown already.
        if (process.env.NODE_ENV !== 'production') console.debug('cleanup endSession:', err);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause / resume. We mute the mic (SDK method) AND signal the agent to
  // stop talking via a contextual update. The ElevenLabs SDK exposes
  // `setMuted` for the mic side and `isMuted` for current state.
  const togglePause = useCallback(async () => {
    try {
      if (!isPaused) {
        // Pausing.
        if (!conversation.isMuted) {
          conversation.setMuted?.(true);
        }
        // Tell the agent we're thinking so it doesn't keep speaking.
        try {
          conversation.sendContextualUpdate?.(
            'The user tapped "Hold on, I\'m thinking". Stay quiet until they resume.',
          );
        } catch (err) {
          // Older SDK builds may not expose sendContextualUpdate.
          if (process.env.NODE_ENV !== 'production') console.debug('pause ctx update:', err);
        }
        setStatus('Paused — tap orb to resume');
        setIsPaused(true);
      } else {
        // Resuming.
        conversation.setMuted?.(false);
        try {
          conversation.sendContextualUpdate?.(
            'The user is ready to continue. Say a brief "ready when you are" acknowledgment then listen.',
          );
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.debug('resume ctx update:', err);
        }
        setStatus('SAGE is listening');
        setIsPaused(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.debug('togglePause failed:', err);
    }
  }, [isPaused, conversation]);

  // Submit a typed message as the user's turn.
  const submitText = useCallback(async (e) => {
    e?.preventDefault?.();
    const text = textDraft.trim();
    if (!text) return;
    try {
      conversation.sendUserMessage?.(text);
      setLiveUserTranscript(text);
      setTextDraft('');
      setShowTextInput(false);
    } catch (err) {
      toast.error('Could not send that message. Try again?');
    }
  }, [textDraft, conversation]);

  // End session + fire-and-forget finalize into our DB.
  //
  // Why fire-and-forget: the backend used to synchronously poll
  // ElevenLabs for the transcript (up to ~60s) before responding —
  // which made the back/end button feel dead for 5+ seconds. The
  // backend now returns 202 immediately and runs the transcript pull
  // in a background task. We still bound the client-side wait with
  // a short safety timeout (1.2s) so a slow network can't trap the
  // user on this screen either.
  const endSession = useCallback(async () => {
    if (isEnding) return;
    setIsEnding(true);
    setStatus('Wrapping up…');

    // Grab the conversation id BEFORE ending the session — some SDK
    // builds clear it right after endSession().
    let convoId = null;
    try {
      convoId = conversation.getId?.();
    } catch (err) {
      // getId may fail on a disposed SDK state — convoId stays null.
      if (process.env.NODE_ENV !== 'production') console.debug('getId:', err);
    }
    try {
      conversation.endSession?.();
    } catch (err) {
      // We're ending anyway; a thrown error here isn't actionable.
      if (process.env.NODE_ENV !== 'production') console.debug('endSession:', err);
    }

    const exitNow = () => {
      // Toast is scoped to the common path — the dashboard will show
      // a "Processing…" badge on the just-ended card until notes land.
      try {
        toast.success('Session saved — notes will appear in a moment');
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('exit toast:', err);
      }
      onExit?.();
    };

    if (!sessionId || !convoId) {
      // Nothing to finalize — exit immediately.
      exitNow();
      return;
    }

    const token = localStorage.getItem('sage_token');
    const finalizePromise = axios.post(
      `${API}/coaching/agent/finalize`,
      { session_id: sessionId, conversation_id: convoId },
      { headers: { Authorization: `Bearer ${token}` } },
    ).catch((err) => {
      if (process.env.NODE_ENV !== 'production') console.debug('finalize error:', err);
      // Even on error the server has already recorded ended_at via the
      // 202-path or the row is unreachable; either way the user exits.
    });

    // Race the request against a 1.2s safety timeout. The 202 response
    // typically lands in ~200ms; the timeout is a hard ceiling so
    // nothing (bad wifi, slow worker) traps the user on this screen.
    const safety = new Promise((resolve) => setTimeout(resolve, 1200));
    await Promise.race([finalizePromise, safety]);
    exitNow();
  }, [conversation, sessionId, isEnding, onExit]);

  // Orb state → particle pulse amplitude.
  const isSpeaking = conversation.isSpeaking;
  const orbPulse = isPaused ? 0.7 : isSpeaking ? 1.3 : 1.0;

  return (
    <div
      data-testid="auto-coaching-root"
      className="min-h-screen bg-black text-white flex flex-col"
    >
      {/* Header — single "Exit Session" labelled button on the right.
          A separate back arrow + X was redundant and confusing on
          mobile; one well-labelled button is clearer. The label is
          hidden visually on very narrow screens to prevent overflow
          but kept available to screen readers. */}
      <header className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/40">
          <span>Auto</span>
          <ThemeToggle size="sm" />
        </div>
        <button
          type="button"
          data-testid="exit-session-button"
          onClick={endSession}
          disabled={isEnding}
          aria-label="Exit session"
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/15 text-white/75 hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isEnding ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span className="text-xs font-mono uppercase tracking-wider">Wrapping up…</span>
            </>
          ) : (
            <>
              <X className="w-4 h-4" aria-hidden="true" />
              <span className="text-xs font-mono uppercase tracking-wider">Exit Session</span>
            </>
          )}
        </button>
      </header>

      {/* Orb */}
      <main className="flex-1 flex flex-col items-center justify-center px-6">
        <SageParticleOrb
          isActive={isSpeaking && !isPaused}
          pulseScale={orbPulse}
          data-testid="sage-orb"
        />
        <div
          data-testid="session-status"
          className="mt-6 text-sm text-white/60 text-center min-h-[1.5rem]"
        >
          {status}
        </div>

        {/* Dead-end escape hatch: when Auto fails to start (e.g. the
            ElevenLabs agent isn't configured on this environment), the
            user otherwise has no path forward. One tap flips their
            profile to Hold-to-Talk and reloads into the classic flow. */}
        {startError && (
          <Button
            data-testid="switch-to-hold-to-talk-cta"
            onClick={async () => {
              const token = localStorage.getItem('sage_token');
              try {
                await axios.put(
                  `${API}/profile`,
                  { auto_mode_enabled: false },
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                window.location.reload();
              } catch (err) {
                if (process.env.NODE_ENV !== 'production') console.debug('mode switch:', err);
                toast.error('Could not switch mode — try Settings on the dashboard.');
              }
            }}
            variant="outline"
            className="mt-4 border-white/20 text-white hover:bg-white/10"
          >
            Use Hold-to-Talk instead
          </Button>
        )}

        {/* Captions (when enabled) */}
        {showCaptions && (
          <div className="mt-6 w-full max-w-md space-y-2 text-sm">
            {liveUserTranscript && (
              <div
                data-testid="caption-user"
                className="text-white/70 italic text-right"
              >
                You: {liveUserTranscript}
              </div>
            )}
            {lastAgentMessage && (
              <div data-testid="caption-sage" className="text-white/90">
                SAGE: {lastAgentMessage}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Controls */}
      <footer className="px-6 pb-8 flex flex-col items-center gap-3">
        <Button
          data-testid="pause-button"
          onClick={togglePause}
          disabled={status === 'Preparing…' || status === 'Connecting to SAGE…' || isEnding}
          className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/10 text-white hover:bg-white/20 touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
          aria-label={isPaused ? 'Resume conversation' : "Hold on, I'm thinking"}
        >
          {isPaused ? (
            <Play className="w-8 h-8" aria-hidden="true" />
          ) : (
            <Pause className="w-8 h-8" aria-hidden="true" />
          )}
        </Button>
        <div className="text-xs text-white/50">
          {isPaused ? 'Tap to resume' : "Hold on, I'm thinking"}
        </div>

        {/* Utility row */}
        <div className="flex items-center gap-6 mt-2 text-xs text-white/60">
          <button
            data-testid="captions-toggle"
            onClick={() => setShowCaptions((v) => !v)}
            className="flex items-center gap-1.5 hover:text-white/90 transition-colors"
          >
            <Captions className="w-4 h-4" aria-hidden="true" />
            {showCaptions ? 'Hide captions' : 'Captions'}
          </button>
          <span className="text-white/20">·</span>
          <button
            data-testid="text-input-toggle"
            onClick={() => setShowTextInput((v) => !v)}
            className="flex items-center gap-1.5 hover:text-white/90 transition-colors"
          >
            <Keyboard className="w-4 h-4" aria-hidden="true" />
            {showTextInput ? 'Hide text' : 'Or type your question'}
          </button>
        </div>

        {/* Text fallback */}
        {showTextInput && (
          <form
            data-testid="text-input-form"
            onSubmit={submitText}
            className="w-full max-w-md mt-3 flex gap-2"
          >
            <input
              data-testid="text-input-field"
              type="text"
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              placeholder="Type your message to SAGE…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/30"
              autoFocus
            />
            <Button
              data-testid="text-input-submit"
              type="submit"
              disabled={!textDraft.trim()}
              className="bg-white text-black hover:bg-white/90 rounded-full px-4"
            >
              Send
            </Button>
          </form>
        )}
      </footer>
    </div>
  );
};

// Wrap in the SDK's ConversationProvider so useConversation's hooks have
// their required context. The provider has to live ABOVE any component
// that calls useConversation / useConversationControls / etc.
const AutoCoachingSession = (props) => (
  <ConversationProvider>
    <AutoCoachingInner {...props} />
  </ConversationProvider>
);

export default AutoCoachingSession;